import {api, RequestParam} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {renderOvertimesPopoverContent, renderTravellingTimesPopoverContent} from "@/features/timeaccounts/timeAccountPopoverUtil";
import {CurrentTimeAccountDashboardPage} from "./currentTimeAccountDashboardPage";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {formatVacation, getCurrentISOOffset, isSameOrAfterDay, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {CurrentTimeAccount, TimeAccountDashboardBalanceMode, User} from "@/shared/timr";
import {FlexTimeIndicator, VacationUnit} from "@/shared/timrTypes";
import {showPdfPreview} from "@/features/pdfPreview/showPdfPreview";

export interface CurrentTimeAccountTableConfig extends BasicDataTableConfig {
	timeAccountBalanceChanged?: () => void;
	showOvertimesColumn: boolean;
	showTravellingTimesColumn: boolean;
	showFlexTimeIndicatorColumn: boolean;
}

export class CurrentTimeAccountTable extends BasicDataTable<CurrentTimeAccount> {

	private static COLUMN_SIZE_USER = "200px";
	private static COLUMN_SIZE_PREVIEW = "100px";

	constructor(public config: CurrentTimeAccountTableConfig) {
		super("/timr/timeaccounts/getCurrentTimeAccounts.json", config);

		this.config.element.on("click", ".previewButton", e => {
			const rowData = this.getRowData($(e.target as HTMLElement).closest("tr"));
			this.showTimeSheetPreview(rowData);
		});
		this.initPopover();
	}

	private initPopover() {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;
		// Only one popover per element, so define it on the parent
		this.config.element.parent().popover({
			trigger: "hover",
			placement: "top",
			selector: `tbody > tr > td`,
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: "<div class=\"popover time-account-popover\" role=\"tooltip\"><div class=\"arrow\"></div><div class=\"popover-content\"></div></div>",
			content: function (this: HTMLElement) {
				const hoveredElement = $(this);
				const currentTimeAccount = that.getRowData(hoveredElement);
				if (!currentTimeAccount) {
					return null;
				}

				if (hoveredElement.is(".overtime-accounts")) {
					// it's not necessary to check SecurityStatus.ifOvertimeAllowanceAllowed(), because td.overtime-accounts is invisible if this is not the case
					return renderOvertimesPopoverContent(currentTimeAccount.overtimes, currentTimeAccount.allowancesOnly);
				} else if (hoveredElement.is(".travelling-time-accounts")) {
					// it's not necessary to check SecurityStatus.ifTravellingTimesAllowed(), because td.travelling-time-accounts is invisible if this is not the case
					return renderTravellingTimesPopoverContent(currentTimeAccount.travellingTimes);
				}
			}
		});
	}

	public filterByModeAndGroupAndUser(mode: TimeAccountDashboardBalanceMode, groupId: number, userId: number) {
		return this.filter(CurrentTimeAccountTable.buildFilterParams(mode, groupId, userId));
	}

	public static buildFilterParams(mode: TimeAccountDashboardBalanceMode, groupId: number, userId: number): RequestParam[] {
		return [
			{name: "groupId", value: groupId},
			{name: "userId", value: userId},
			{name: "timeAccountDashboardBalanceMode", value: mode},
			{name: "timeZone", value: getCurrentISOOffset()}
		];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			pageLength: 20
		};
	}

	/**
	 * Returns the column definitions for the current time account table.
	 * When trustBasedWorkingTime is active an info text will be visible (colspan of 3 columns)
	 * and lastWorkDurationTotalBalance, workDurationBalance, workDurationTotalBalance are removed
	 *
	 * @protected
	 */
	protected getColumnSettings(): TypedColumnSettings<CurrentTimeAccount>[] {
		return [
			{
				data: "user.fullname",
				orderable: false,
				width: CurrentTimeAccountTable.COLUMN_SIZE_USER,
				render: BasicDataTable.renderText
			},
			{data: "lastTimeAccountBalanceDate", className: "text-right", orderable: false, render: BasicDataTable.renderDate},
			{
				data: "flexTimeIndicator",
				className: "text-right",
				orderable: false,
				render: this.renderFlexTimeIndicator,
				visible: this.config.showFlexTimeIndicatorColumn
			},
			{
				data: "lastWorkDurationTotalBalance",
				className: "slim-numbers-column border-left border-left-grey-300",
				orderable: false,
				render: (d, t, r, m) => this.renderLastWorkDurationColumn(d, t, r, m),
				createdCell: (td, c, r: CurrentTimeAccount) => !r.workingTimeTracking || r.trustBasedWorkingTime ? $(td).attr("colspan", "3").addClass("text-center border-right border-right-grey-300") : null
			},
			{
				data: "workDurationBalance",
				className: "slim-numbers-column",
				orderable: false,
				render: BasicDataTable.renderHours,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.workingTimeTracking || r.trustBasedWorkingTime) {
						$(td).remove();
					}
				}
			},
			{
				data: "workDurationTotalBalance",
				orderable: false,
				className: "pr-15 text-right border-right border-right-grey-300",
				render: BasicDataTable.renderHours,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.workingTimeTracking || r.trustBasedWorkingTime) {
						$(td).remove();
					}
				}
			},
			{
				data: "overtimeSum",
				orderable: false,
				className: "overtime-accounts text-center border-right border-right-grey-300",
				render: BasicDataTable.renderHours,
				visible: this.config.showOvertimesColumn
			},
			{
				data: "travellingTimeSum",
				orderable: false,
				className: "travelling-time-accounts text-center border-right border-right-grey-300",
				render: BasicDataTable.renderHours,
				visible: this.config.showTravellingTimesColumn
			},
			{
				data: "vacationYearStart",
				className: "slim-numbers-column",
				orderable: false,
				render: (d, t, r, m) => this.renderVacationYearStartColumn(d, t, r, m),
				createdCell: (td, c, r: CurrentTimeAccount) => !r.vacationAccount ? $(td).attr("colspan", "5").addClass("text-center border-right border-right-grey-300") : null
			},
			{
				data: "vacationConsumed", className: "slim-numbers-column", orderable: false, render: this.renderVacation,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "vacationPlanned", className: "slim-numbers-column", orderable: false, render: this.renderVacation,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "vacationLeft", className: "slim-numbers-column", orderable: false, render: this.renderVacation,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "vacationUnit",
				orderable: false,
				className: "border-right border-right-grey-300",
				render: this.renderVacationUnit,
				createdCell: (td, c, r: CurrentTimeAccount) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "user",
				orderable: false,
				className: "text-right text-nowrap",
				width: CurrentTimeAccountTable.COLUMN_SIZE_PREVIEW,
				render: this.renderPreviewButton
			}
		];
	}

	/**
	 * Displays the last work duration column
	 * If working time tracking is disabled or trust based working time is active an info will be displayed
	 */
	private renderLastWorkDurationColumn(data: number, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		if (row.workingTimeTracking && !row.trustBasedWorkingTime) {
			return BasicDataTable.renderHours(data, type, row, meta);
		} else {
			const message = !row.workingTimeTracking ? messages["reports.export.timeAccount.workingTimeTracking"] : messages["reports.export.timeAccount.trustBasedWorkingTime"];
			return `<div class="text-grey text-center"><i class="fal fa-info-circle position-left"></i>${message}</div>`;
		}
	}

	/**
	 * Displays the vacation year start column
	 * If vacation account is disabled an info will be displayed
	 */
	private renderVacationYearStartColumn(data: string, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		return row.vacationAccount ? BasicDataTable.renderDateWithoutYear(data, type, row, meta) :
			`<div class="text-grey text-center"><i class="fal fa-info-circle position-left"></i>${messages["reports.export.timeAccount.vacationAccount"]}</div>`;
	}

	private renderVacationUnit(this: void, data: VacationUnit, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		if (row.vacationConsumed != null) {
			return messages["vacationUnit." + data];
		} else {
			return "";
		}
	}

	private renderFlexTimeIndicator(this: void, data: FlexTimeIndicator, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		if (!data) {
			return "";
		} else {
			return `
				<div>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" height="24" class="display-block ml-auto">
						<ellipse class="flex-time-indicator-fill-${data?.toLowerCase()}" cx="13" cy="13" rx="12" ry="12"></ellipse>
					</svg>
				</div>
				`;
		}
	}

	private showTimeSheetPreview(rowData: CurrentTimeAccount) {
		const mode = BrowserStorageSettings.getTimeAccountDashboardBalanceMode(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT) || "TODAY";
		const date = mode === "YESTERDAY" ? Temporal.Now.plainDateISO().subtract({days: 1}) : Temporal.Now.plainDateISO();
		if (rowData && isSameOrAfterDay(parseISODate(rowData.lastTimeAccountBalanceDate), date)) {
			BootstrapModal.info({
				title: "report.timeAccountDashboard.preview.alreadyBalanced.title",
				message: "report.timeAccountDashboard.preview.alreadyBalanced.message"
			});
		} else {
			const userId = rowData?.user?.id;
			if (userId == null) {
				return;
			}

			api("timeaccounts/previewTimeSheetOfUser.json")
				.get({
					userId,
					timeAccountDashboardBalanceMode: mode,
					timeZone: getCurrentISOOffset()
				})
				.done(data => {
					showPdfPreview(`${messages["currentTimeAccount.preview"]}: ${rowData.user.fullname}`, data.exportJobId, messages["downloadStatus.loadingMessage.timeAccount"]);
				});
		}
	}

	private renderPreviewButton(this: void, data: User, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		return row.workingTimeTracking && !row.trustBasedWorkingTime ? `<a class="previewButton">${messages["currentTimeAccount.preview"]}</a>` : "";
	}

	private renderVacation(this: void, data: number, type: string, row: CurrentTimeAccount, meta: DataTables.CellMetaSettings): string {
		return data != null ? formatVacation(data, row.vacationUnit) : "";
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "desc"]];
	}
}
